import { ErrorState, SkeletonLine } from "@l30/ui";
import { createRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";

import { isPortalAuthError } from "../api/portal-client.js";
import { usePortalMe } from "../api/portal.js";
import { PortalHeader } from "../components/portal/portal-header.js";
import { PortalOrientation } from "../components/portal/portal-orientation.js";
import { PortalQueryError } from "../components/portal-query-error.js";
import { brandForPartner } from "../lib/partner-brand.js";
import { clearOrientationSeen, markOrientationSeen } from "../lib/portal-copy.js";
import { portalSignInPath } from "../lib/portal-tenant-path.js";

import { tenantScopeRoute } from "./tenant-scope.js";

/**
 * Portal layout — gates every authenticated portal route on
 * `GET /api/portal/me`. The browser carries the portal session
 * cookie; the server returns 200 if it's valid, 401 otherwise.
 *
 * Transition33 partners (e.g. gop) get `data-partner-brand` so
 * globals.css applies the locked Figma token pack.
 */
export const portalRoute = createRoute({
  getParentRoute: () => tenantScopeRoute,
  id: "portal-shell",
  component: PortalShell,
});

function PortalShell() {
  const navigate = useNavigate();
  const { tenantSlug } = portalRoute.useParams();
  const meQuery = usePortalMe();
  const isAuthError = isPortalAuthError(meQuery.error);
  const signInPath = portalSignInPath(tenantSlug);
  const brand = brandForPartner(tenantSlug);
  const [replayOrientation, setReplayOrientation] = useState(false);
  const isTransition33 = brand.kind === "transition33";

  useEffect(() => {
    if (isAuthError) {
      void navigate({ href: signInPath, replace: true });
    }
  }, [isAuthError, navigate, signInPath]);

  if (meQuery.isLoading || isAuthError) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="w-full max-w-sm space-y-3 px-6">
          <SkeletonLine className="w-1/2" />
          <SkeletonLine />
          <SkeletonLine className="w-2/3" />
        </div>
      </div>
    );
  }

  if (meQuery.isError) {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6">
        <div className="w-full max-w-lg">
          <PortalQueryError
            error={meQuery.error}
            title="Couldn't load your session"
            retry={() => void meQuery.refetch()}
          />
        </div>
      </div>
    );
  }

  if (!meQuery.data) {
    return null;
  }

  return (
    <div className="flex min-h-screen flex-col bg-background" data-partner-brand={brand.kind}>
      {replayOrientation ? (
        <PortalOrientation
          isTransition33={isTransition33}
          onContinue={() => {
            markOrientationSeen(sessionStorage);
            setReplayOrientation(false);
          }}
        />
      ) : null}
      <PortalHeader
        me={meQuery.data}
        onReplayOrientation={() => {
          clearOrientationSeen(sessionStorage);
          setReplayOrientation(true);
        }}
      />
      <main className="t33-content-width mx-auto w-full max-w-5xl flex-1 px-6 py-8 md:px-10">
        <ErrorBoundary FallbackComponent={PortalErrorFallback}>
          <Outlet />
        </ErrorBoundary>
      </main>
      <PortalFooter />
    </div>
  );
}

function PortalFooter() {
  const { tenantSlug } = portalRoute.useParams();
  const brand = brandForPartner(tenantSlug);
  const year = new Date().getFullYear();
  const isT33 = brand.kind === "transition33";

  if (!isT33) {
    return (
      <footer className="mt-auto border-t border-border px-6 py-3 text-center text-xs text-fg-subtle">
        &copy; {year} Latitude30
      </footer>
    );
  }

  return (
    <footer className="mt-auto border-t border-border px-6 py-4 text-[11px] leading-relaxed text-fg-subtle md:px-10">
      <div className="t33-content-width mx-auto flex max-w-5xl flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1 font-mono uppercase tracking-wide">
          <div>Transition33 · Government of Pakistan · Latitude30</div>
          <div className="normal-case tracking-normal">
            &copy; {year} Latitude30 Advisors Ltd. All rights reserved. Transition33 is a joint
            programme of the Government of Pakistan and Latitude30.
          </div>
        </div>
        <div className="shrink-0 font-mono uppercase tracking-wide">
          Security class: Restricted A
        </div>
      </div>
    </footer>
  );
}

function PortalErrorFallback({ error }: FallbackProps) {
  return (
    <div className="mx-auto max-w-2xl">
      {error instanceof Error ? (
        <PortalQueryError
          error={error}
          title="Something went wrong"
          retry={() => window.location.reload()}
        />
      ) : (
        <ErrorState
          title="Something went wrong"
          message="An unexpected error occurred."
          retry={() => window.location.reload()}
        />
      )}
    </div>
  );
}
